import * as XLSX from "xlsx";
import {
  DATA_SHEET_COL_TYPE,
  type DataSheetColumn,
  type DataSheetRecord,
  type LookupItem,
  type LookupsResponse,
} from "../api/dataSheetsApi";

/**
 * Pure (UI-free) helpers for importing/exporting Data Sheet records as
 * CSV or Excel. Everything here runs client-side on top of the existing
 * data-sheets API: `lookups` gives the tenant-scoped id↔name lists used
 * to resolve reference columns, `listRecords` feeds the export, and the
 * record create/update/delete endpoints perform the import.
 *
 * Reference-typed columns (hunt group / agent / voice / weekplan) can be
 * treated as either a *value* (the human name, e.g. "Support agents") or
 * a *reference* (the stored id). Scalar columns pass through unchanged
 * apart from light normalisation (checkbox → "0"/"1", date → YYYY-MM-DD,
 * time → HH:mm, decimal comma → dot).
 */

export type RefMode = "value" | "reference";
export type ImportMode = "append" | "replace" | "upsert";
export type ExportFormat = "csv" | "xlsx";

export interface ParsedFile {
  fileName: string;
  headers: string[];
  rows: string[][];
}

export function isReferenceType(typeId: number): boolean {
  return (
    typeId === DATA_SHEET_COL_TYPE.huntGroup ||
    typeId === DATA_SHEET_COL_TYPE.agent ||
    typeId === DATA_SHEET_COL_TYPE.voice ||
    typeId === DATA_SHEET_COL_TYPE.weekplan
  );
}

export function lookupListFor(typeId: number, lookups: LookupsResponse): LookupItem[] {
  switch (typeId) {
    case DATA_SHEET_COL_TYPE.huntGroup: return lookups.huntGroups;
    case DATA_SHEET_COL_TYPE.agent: return lookups.agents;
    case DATA_SHEET_COL_TYPE.voice: return lookups.voices;
    case DATA_SHEET_COL_TYPE.weekplan: return lookups.weekplans;
    default: return [];
  }
}

/** Case-insensitive, trimmed, whitespace-collapsed key for name matching. */
export function normaliseName(s: string | null | undefined): string {
  return (s ?? "").trim().toLowerCase().replace(/\s+/g, " ");
}

// ---------------------------------------------------------------------------
// Parsing
// ---------------------------------------------------------------------------

/**
 * Reads a CSV/XLSX file. Row 1 = headers; fully empty rows are dropped;
 * every cell becomes a trimmed string.
 *
 * Excel: cells are read *raw* (numbers as numbers, dates as Dates) and
 * stringified by us — reading the *formatted* text would hand us Excel's
 * display form, e.g. `4.41201E+11` for a phone number in a "General"
 * cell. CSV: parsed by our own delimiter-aware parser so nothing is ever
 * auto-typed (long digit strings and leading zeros survive verbatim).
 */
export async function parseFile(file: File): Promise<ParsedFile> {
  const ext = file.name.toLowerCase().split(".").pop() ?? "";
  let aoa: unknown[][];
  if (ext === "csv" || ext === "txt" || ext === "tsv") {
    aoa = parseDelimitedText(await file.text());
  } else {
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: "array", cellDates: true });
    const ws = wb.Sheets[wb.SheetNames[0]];
    aoa = ws ? XLSX.utils.sheet_to_json<unknown[]>(ws, { header: 1, raw: true, defval: "" }) : [];
  }
  const [head = [], ...body] = aoa;
  const headers = head.map(cellToString);
  const rows = body
    .map((r) => headers.map((_, i) => cellToString(r[i])))
    .filter((r) => r.some((c) => c !== ""));
  return { fileName: file.name, headers, rows };
}

/** Stringifies a raw SheetJS cell value without scientific notation. */
export function cellToString(v: unknown): string {
  if (v == null) return "";
  if (v instanceof Date) return dateCellToString(v);
  if (typeof v === "number") return numberToPlainString(v);
  if (typeof v === "boolean") return v ? "true" : "false";
  return String(v).trim();
}

/** 441200866992 → "441200866992" (never "4.41201E+11"); 3.5 → "3.5". */
export function numberToPlainString(n: number): string {
  if (!Number.isFinite(n)) return "";
  return n.toLocaleString("en-US", { useGrouping: false, maximumFractionDigits: 15 });
}

function dateCellToString(d: Date): string {
  if (isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const date = `${y}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
  const time = `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  // Excel stores a time-only cell as a fraction of day 0 (year 1899/1900).
  if (y < 1905) return time;
  return time === "00:00" ? date : `${date} ${time}`;
}

/** RFC-4180-style CSV parser (quotes, escaped quotes, CRLF, BOM). The
 *  delimiter is sniffed from the header line: `;` (NL Excel), tab or `,`. */
export function parseDelimitedText(text: string): string[][] {
  const src = text.replace(/^﻿/, "");
  const first = src.split(/\r?\n/, 1)[0] ?? "";
  const count = (ch: string) => first.split(ch).length - 1;
  const delim = count(";") > count(",") ? ";" : count("\t") > count(",") ? "\t" : ",";
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let inQuotes = false;
  for (let i = 0; i < src.length; i++) {
    const c = src[i];
    if (inQuotes) {
      if (c === '"') {
        if (src[i + 1] === '"') { field += '"'; i++; } else inQuotes = false;
      } else field += c;
    } else if (c === '"') inQuotes = true;
    else if (c === delim) { row.push(field); field = ""; }
    else if (c === "\n") { row.push(field); rows.push(row); row = []; field = ""; }
    else if (c !== "\r") field += c;
  }
  if (field !== "" || row.length > 0) { row.push(field); rows.push(row); }
  return rows;
}

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

export function buildExportRows(
  columns: DataSheetColumn[],
  records: DataSheetRecord[],
  modes: Record<number, RefMode>,
): { headers: string[]; rows: string[][] } {
  const headers = columns.map((c) => c.name ?? "");
  const rows = records.map((r) =>
    columns.map((c) => {
      const raw = r.values[c.slot - 1] ?? "";
      if (!isReferenceType(c.typeId)) return raw;
      if ((modes[c.slot] ?? "value") === "reference") return raw;
      const display = r.displays[c.slot - 1] ?? "";
      // Prefer the resolved name; fall back to the raw id when the
      // target was deleted so the export never silently loses data.
      return display && display !== raw ? display : raw;
    }),
  );
  return { headers, rows };
}

export function downloadExport(
  format: ExportFormat,
  baseName: string,
  headers: string[],
  rows: string[][],
): void {
  const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Data");
  const safe = baseName.replace(/[^\w\- ]+/g, "_").trim() || "data-sheet";
  XLSX.writeFile(wb, `${safe}.${format}`, { bookType: format });
}

// ---------------------------------------------------------------------------
// Import: mapping + resolution
// ---------------------------------------------------------------------------

/** Proposes a file-column → slot mapping by matching header names to
 *  column names (normalised). Each slot is used at most once. */
export function suggestMapping(headers: string[], columns: DataSheetColumn[]): (number | null)[] {
  const bySlotName = new Map<string, number>();
  for (const c of columns) {
    if ((c.name ?? "").trim()) bySlotName.set(normaliseName(c.name), c.slot);
  }
  const used = new Set<number>();
  return headers.map((h) => {
    const slot = bySlotName.get(normaliseName(h));
    if (slot != null && !used.has(slot)) {
      used.add(slot);
      return slot;
    }
    return null;
  });
}

export type CellStatus = "ok" | "empty" | "unresolved";

export interface ResolvedCell {
  raw: string;
  /** Value to store (stringified id for references). null = empty. */
  value: string | null;
  /** Human-readable value for the preview. */
  display: string;
  status: CellStatus;
}

const ok = (raw: string, value: string, display?: string): ResolvedCell => ({
  raw,
  value,
  display: display ?? value,
  status: "ok",
});
const empty = (raw: string): ResolvedCell => ({ raw, value: null, display: "", status: "empty" });
const unresolved = (raw: string): ResolvedCell => ({ raw, value: null, display: raw, status: "unresolved" });

/** Resolves one imported cell against the column type + chosen mode. */
export function resolveCell(
  column: DataSheetColumn,
  mode: RefMode,
  rawInput: string,
  lookups: LookupsResponse,
): ResolvedCell {
  const raw = (rawInput ?? "").trim();
  const t = column.typeId;

  if (isReferenceType(t)) {
    if (!raw) return empty(raw);
    const list = lookupListFor(t, lookups);
    if (mode === "reference") {
      const hit = list.find((i) => String(i.id) === raw);
      return hit ? ok(raw, String(hit.id), hit.name) : unresolved(raw);
    }
    const key = normaliseName(raw);
    const hits = list.filter((i) => normaliseName(i.name) === key);
    // Exactly one match resolves; zero or ambiguous stays unresolved so
    // the user picks explicitly in the preview.
    return hits.length === 1 ? ok(raw, String(hits[0].id), hits[0].name) : unresolved(raw);
  }

  if (t === DATA_SHEET_COL_TYPE.checkbox) {
    const truthy = ["1", "true", "yes", "ja", "y", "x", "on", "waar"];
    const v = truthy.includes(normaliseName(raw)) ? "1" : "0";
    return ok(raw, v, v === "1" ? "Yes" : "No");
  }
  if (t === DATA_SHEET_COL_TYPE.number) {
    if (!raw) return empty(raw);
    const n = raw.replace(",", ".");
    return /^-?\d+(\.\d+)?$/.test(n) ? ok(raw, n) : unresolved(raw);
  }
  if (t === DATA_SHEET_COL_TYPE.date) {
    if (!raw) return empty(raw);
    const iso = toIsoDate(raw);
    return iso ? ok(raw, iso) : unresolved(raw);
  }
  if (t === DATA_SHEET_COL_TYPE.time) {
    if (!raw) return empty(raw);
    const iso = toIsoTime(raw);
    return iso ? ok(raw, iso) : unresolved(raw);
  }
  return raw ? ok(raw, raw) : empty(raw);
}

const pad2 = (n: number | string) => String(n).padStart(2, "0");

/** Accepts YYYY-MM-DD, D-M-YYYY / D/M/YYYY / D.M.YYYY (day-first, NL) or
 *  anything Date can parse. Returns YYYY-MM-DD or null. */
export function toIsoDate(s: string): string | null {
  let m = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(s);
  if (m) return `${m[1]}-${pad2(m[2])}-${pad2(m[3])}`;
  m = /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/.exec(s);
  if (m) return `${m[3]}-${pad2(m[2])}-${pad2(m[1])}`;
  const d = new Date(s);
  if (isNaN(d.getTime())) return null;
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** Accepts H:mm or HH:mm(:ss). Returns HH:mm or null. */
export function toIsoTime(s: string): string | null {
  const m = /^(\d{1,2}):(\d{2})(?::\d{2})?$/.exec(s);
  if (!m) return null;
  const h = Number(m[1]);
  const mi = Number(m[2]);
  if (h > 23 || mi > 59) return null;
  return `${pad2(h)}:${pad2(mi)}`;
}

// ---------------------------------------------------------------------------
// Import: upsert key matching
// ---------------------------------------------------------------------------

/** Finds an existing record whose stored values equal the given values on
 *  every key slot (composite key, compared as trimmed strings). */
export function findExistingByKeys(
  records: DataSheetRecord[],
  keySlots: number[],
  values: (string | null)[],
): DataSheetRecord | undefined {
  if (keySlots.length === 0) return undefined;
  const norm = (v: string | null | undefined) => (v ?? "").trim();
  return records.find((r) => keySlots.every((s) => norm(r.values[s - 1]) === norm(values[s - 1])));
}
